/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from "react";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import getConfig from "next/config";
import { NextSeo } from "next-seo";
import {
    PageLayout, JobSearchRequest, JobSearchResults, DirectApply
} from "@themuse/design-system";
import { AppProvider } from "hooks/AppContext";
import { AppState } from "interfaces/context/appContextInterfaces";
import {
    JobCategory, JobLevel, SingleJob
} from "interfaces/api/apiInterfaces";
import {
    genBaseProps, getJobTrackingProps, museTrack
} from "utils/tracking";
import { getActiveFeatureFlags } from "utils/featureFlags";
import { getCompressedJob } from "utils/orm";
import { jobAlertsSubscribeRequest, searchJobs } from "utils/api";
import { formatLog } from "utils/logging";
import { useContentPercentViewed } from "hooks/useContentPercentViewed";
import { FeatureFlagsProvider } from "hooks/FeatureFlagsContext";
import { JobIndividualPageLayout } from "components/JobIndividualPageLayout";
import { JobIndividualHeader } from "components/JobIndividualHeader";
import { JobIndividualBody } from "components/JobIndividualBody";
import { JobIndividualSidebar } from "components/JobIndividualSidebar";
import { JobPostJsonLd } from "components/JobPostJsonLd";
import { DirectApplyModal } from "components/DirectApplyModal";
import { UserInit } from "components/UserInit";
import styles from "styles/Home.module.scss";

interface InitialAppParams extends AppState {
    redirectPath?: string;
    queryString? : string;
}

/**
 * Retrieves job and company data from the server-side API and passes it as props to the 'Home' component.
 */
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    let notFound = false;
    const { job, company } = await getCompressedJob(context);

    const initialAppParams: InitialAppParams = {
        job,
        company
    };

    if ((!initialAppParams.job || !initialAppParams.company) && !initialAppParams.redirectPath) {
        notFound = true;
    }

    if (initialAppParams.job?.state === "archived") {
        const msg = "Intentionally returned an archived job as a custom 404.";
        console.info(msg, JSON.stringify(formatLog({ context, contextToLogMap: null, error: null })));
        context.res.statusCode = 404;
    }

    const redirectConfig = initialAppParams.redirectPath ? {
        redirect: {
            destination: initialAppParams.queryString
                ? `${initialAppParams.redirectPath}?${initialAppParams.queryString}`
                : initialAppParams.redirectPath,
            permanent: true
        }
    } : {};

    return {
        notFound,
        ...redirectConfig,
        props: {
            statusCode: context.res.statusCode,
            initialAppParams,
            featureFlags: {
                ...getActiveFeatureFlags(context)
            }
        }
    };
};

/**
 * Track user interaction with the search bar in the header of the webpage by calling the 'museTrack'.
 */
const handleSearchSubmit = () => {
    museTrack("Header Link Clicked", {
        ...genBaseProps(),
        header_link: "search_bar",
        link_name: "search_bar"
    });
};

/**
 * Retrieve a list of similar jobs based on the input job and pass it to a callback function.
 */
const getSimilarJobs = async (job: SingleJob, callback: (similarJobsSearchResult: JobSearchResults) => void) => {
    if (job !== null) {
        const similarJobsRequest: JobSearchRequest = {
            query: job?.name?.split(" ").slice(0, 3).join(" "),
            level: job?.levels?.map((level: JobLevel) => level?.value === "senior" ? "senior,management" : level?.value).join(","),
            limit: 4
        };
        try {
            const similarJobsSearchResult = await searchJobs(similarJobsRequest);
            callback(similarJobsSearchResult);
        } catch (error) {
            const msg = `Error requesting the similar jobs at ${error?.request?.path}.`;
            const err = JSON.stringify(formatLog({ error }));
            console.warn(msg, err);
        }
    }
};

/**
 * A React component that renders the individual job page. Its main objective is to display the job details, including
 * the job, and company details.
 */
const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const trackedContentItems = useContentPercentViewed();
    const { publicRuntimeConfig: { museWebHost } } = getConfig();
    const { initialAppParams, statusCode, featureFlags } = props;
    const initialAppState = initialAppParams;
    const pageLayoutProps: React.ComponentProps<typeof PageLayout> = {
        activePath: "/search",
        headerClassName: styles.pageHeader,
        pageName: styles.pageWrapper,
        mainClassName: styles.main,
        enableSearch: true,
        searchUrl: "/search",
        onSearchSubmit: handleSearchSubmit,
        showSubMenus: true
    };
    const { job, company } = initialAppState;
    const {
        ats_info,
        locations = [],
        apply_link = "",
        categories = [],
        state = "",
        name: jobName = "",
        short_name: jobShortName = ""
    } = job;
    const {
        name: companyName = "",
        short_name: companyShortName = "",
        legacy_id = ""
    } = company;

    const { directApplyPath = "", directApplyService = "" } = ats_info || {};
    const jobTrackingProps = getJobTrackingProps(job, company);
    const physicalLocations = locations.filter((location) => location?.value !== "Flexible / Remote");
    const firstLocation = physicalLocations?.[0]?.value ?? "";
    const firstlocationDisplay = firstLocation ? ` located in ${firstLocation}, ` : ", ";
    const canonical = `${museWebHost}/jobs/${companyShortName}/${jobShortName}`;
    // eslint-disable-next-line max-len
    const description = `Find our ${jobName} job description for ${companyName}${firstlocationDisplay} as well as other career opportunities that the company is hiring for.`;
    const noIndex = state !== "live";
    const title = `${jobName} at ${companyName} | The Muse`;

    // This controls th direct apply modal and is passed down to both the modal and the apply buttons.
    const [isDirectApplyModalOpen, setIsDirectApplyModalOpen] = React.useState<boolean>(false);

    // if we pass a function use it otherwise we are a normal link.
    const setIsOpen = directApplyService ? setIsDirectApplyModalOpen : null;

    React.useEffect(() => {
        if (trackedContentItems?.length > 0) {
            museTrack("Job Scrolled", {
                ...jobTrackingProps,
                ...trackedContentItems[trackedContentItems.length - 1]
            });
        }
    }, [trackedContentItems]);

    const iframeApplyProps = {
        isOpen: isDirectApplyModalOpen,
        setIsOpen,
        link: apply_link
    };

    const directApplyProps = {
        companyId: `${legacy_id}`,
        companyName,
        endpoint: directApplyPath ?? "",
        isOpen: isDirectApplyModalOpen,
        jobCategory: categories?.[0] ?? {} as JobCategory,
        jobName,
        link: apply_link,
        locations,
        setIsOpen,
        signUpCallBack: jobAlertsSubscribeRequest,
        atsJobId: ats_info?.id,
        service: directApplyService
    };

    useEffect(() => {
        museTrack("Page Loaded", { status_code: statusCode });
        museTrack("Job Individual Page Loaded", jobTrackingProps);
    }, []);
    return (
        <FeatureFlagsProvider featureFlags={featureFlags}>
            <AppProvider {...initialAppState}>
                <NextSeo {...{
                    title,
                    description,
                    canonical,
                    openGraph: {
                        url: canonical,
                        title,
                        description,
                    },
                    noindex: noIndex
                }}
                />
                <JobPostJsonLd isDirectApply={!!directApplyService} />
                <UserInit>
                    <JobIndividualPageLayout {...pageLayoutProps}>
                        <JobIndividualHeader />
                        <JobIndividualBody setIsOpen={setIsOpen} />
                        <JobIndividualSidebar getSimilarJobs={getSimilarJobs} setIsOpen={setIsOpen} />
                        {["mega", "merge"].includes(directApplyService) && <DirectApply {...directApplyProps} />}
                        {directApplyService === "iframe" && <DirectApplyModal {...iframeApplyProps} />}
                    </JobIndividualPageLayout>
                </UserInit>
            </AppProvider>
        </FeatureFlagsProvider>
    );
};

export default Home;
